import React, { useState, useEffect, useCallback } from 'react';
import { Zap, Plus, Edit2, Trash2, Search, Gauge, TrendingUp, CheckCircle2, Circle, Camera } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const today = () => new Date().toISOString().slice(0, 10);
const currentBillingMonth = () => new Date().toISOString().slice(0, 7);

const Electricity = () => {
  const { token } = useAuth();
  const [tab, setTab] = useState('readings');
  const [meters, setMeters] = useState([]);
  const [readings, setReadings] = useState([]);
  const [properties, setProperties] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [propertyFilter, setPropertyFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [paidFilter, setPaidFilter] = useState('');
  const [search, setSearch] = useState('');

  const [showMeterForm, setShowMeterForm] = useState(false);
  const [editingMeter, setEditingMeter] = useState(null);
  const [deletingMeter, setDeletingMeter] = useState(null);

  const [showReadingForm, setShowReadingForm] = useState(false);
  const [editingReading, setEditingReading] = useState(null);
  const [preselectMeter, setPreselectMeter] = useState('');
  const [deletingReading, setDeletingReading] = useState(null);

  const [deleteLoading, setDeleteLoading] = useState(false);

  const h = { headers: { Authorization: `Bearer ${token}` } };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const mq = propertyFilter ? `?property_id=${propertyFilter}` : '';
      const rqs = [
        propertyFilter && `property_id=${propertyFilter}`,
        monthFilter && `billing_month=${monthFilter}`,
        paidFilter === 'paid' && `paid=true`,
        paidFilter === 'unpaid' && `paid=false`,
      ].filter(Boolean).join('&');
      const [mRes, rRes, pRes, sRes] = await Promise.all([
        axios.get(`${API}/electricity/meters${mq}`, h),
        axios.get(`${API}/electricity/readings${rqs ? '?' + rqs : ''}`, h),
        axios.get(`${API}/properties`, h),
        axios.get(`${API}/electricity/summary${rqs ? '?' + rqs : ''}`, h),
      ]);
      setMeters(mRes.data);
      setReadings(rRes.data);
      setProperties(pRes.data);
      setSummary(sRes.data);
    } catch {
      toast({ title: 'Error', description: 'Failed to load electricity data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, propertyFilter, monthFilter, paidFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDeleteMeter = async () => {
    if (!deletingMeter) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/electricity/meters/${deletingMeter.id}`, h);
      toast({ title: 'Meter deleted', description: 'All its readings were also removed.' });
      setDeletingMeter(null); fetchData();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Delete failed', variant: 'destructive' });
    } finally { setDeleteLoading(false); }
  };

  const handleDeleteReading = async () => {
    if (!deletingReading) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/electricity/readings/${deletingReading.id}`, h);
      toast({ title: 'Reading deleted' });
      setDeletingReading(null); fetchData();
    } catch (e) {
      toast({ title: 'Cannot delete', description: e.response?.data?.detail || 'Delete failed', variant: 'destructive' });
    } finally { setDeleteLoading(false); }
  };

  const togglePaid = async (r) => {
    try {
      const ep = r.paid ? 'mark-unpaid' : 'mark-paid';
      await axios.post(`${API}/electricity/readings/${r.id}/${ep}`, {}, h);
      fetchData();
    } catch {
      toast({ title: 'Failed', variant: 'destructive' });
    }
  };

  const filteredReadings = readings.filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (r.meter_number || '').toLowerCase().includes(s)
      || (r.property_name || '').toLowerCase().includes(s)
      || (r.tenant_name || '').toLowerCase().includes(s)
      || (r.billing_month || '').toLowerCase().includes(s);
  });

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="electricity-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Zap className="text-yellow-500" /> Electricity
          </h1>
          <p className="text-gray-600 mt-1">Track meter readings and monthly electricity bills per property or room.</p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="add-meter-btn"
            variant="outline"
            onClick={() => { setEditingMeter(null); setShowMeterForm(true); }}
          >
            <Gauge size={16} className="mr-2" /> Add Meter
          </Button>
          <Button
            data-testid="add-reading-btn"
            onClick={() => { setEditingReading(null); setPreselectMeter(''); setShowReadingForm(true); }}
            className="bg-yellow-600 hover:bg-yellow-700"
            disabled={meters.length === 0}
          >
            <Plus size={16} className="mr-2" /> Add Reading
          </Button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <Card className="p-4 border-l-4 border-l-yellow-500">
            <p className="text-xs text-gray-500">Meters</p>
            <p className="text-2xl font-bold text-gray-900 mt-0.5">{summary.total_meters}</p>
          </Card>
          <Card className="p-4 border-l-4 border-l-blue-500">
            <p className="text-xs text-gray-500">Readings</p>
            <p className="text-2xl font-bold text-gray-900 mt-0.5">{summary.total_readings}</p>
          </Card>
          <Card className="p-4 border-l-4 border-l-indigo-500">
            <p className="text-xs text-gray-500">Units Consumed</p>
            <p className="text-2xl font-bold text-indigo-700 mt-0.5">{summary.total_units?.toLocaleString()}</p>
            <p className="text-xs text-gray-400">kWh</p>
          </Card>
          <Card className="p-4 border-l-4 border-l-green-500">
            <p className="text-xs text-gray-500">Collected</p>
            <p className="text-2xl font-bold text-green-700 mt-0.5">₹{summary.collected?.toLocaleString()}</p>
          </Card>
          <Card className="p-4 border-l-4 border-l-red-500">
            <p className="text-xs text-gray-500">Pending</p>
            <p className="text-2xl font-bold text-red-700 mt-0.5">₹{summary.pending?.toLocaleString()}</p>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b">
        {['readings', 'meters'].map(t => (
          <button
            key={t}
            data-testid={`electricity-tab-${t}`}
            onClick={() => setTab(t)}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 -mb-px transition capitalize ${
              tab === t ? 'border-yellow-500 text-yellow-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t === 'readings' ? 'Readings' : 'Meters'} ({t === 'readings' ? readings.length : meters.length})
          </button>
        ))}
      </div>

      {/* Filters row */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            data-testid="electricity-search"
            type="text"
            placeholder={tab === 'readings' ? 'Search by meter, property, tenant, month...' : 'Filter by property below'}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-yellow-500"
          />
        </div>
        <select
          data-testid="electricity-property-filter"
          value={propertyFilter}
          onChange={e => setPropertyFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          <option value="">All Properties</option>
          {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        {tab === 'readings' && (
          <>
            <input
              data-testid="electricity-month-filter"
              type="month"
              value={monthFilter}
              onChange={e => setMonthFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <select
              data-testid="electricity-paid-filter"
              value={paidFilter}
              onChange={e => setPaidFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">All</option>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
            </select>
          </>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="w-12 h-12 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div></div>
      ) : tab === 'readings' ? (
        filteredReadings.length === 0 ? (
          <Card className="p-12 text-center" data-testid="readings-empty">
            <Zap size={56} className="mx-auto text-gray-300 mb-3" />
            <h3 className="text-xl font-semibold mb-2">No readings yet</h3>
            <p className="text-gray-600 mb-4">Log monthly meter readings to compute bills automatically.</p>
            {meters.length === 0 ? (
              <Button onClick={() => { setEditingMeter(null); setShowMeterForm(true); }} className="bg-yellow-600 hover:bg-yellow-700">
                <Gauge size={16} className="mr-2" /> Add Your First Meter
              </Button>
            ) : (
              <Button onClick={() => { setEditingReading(null); setShowReadingForm(true); }} className="bg-yellow-600 hover:bg-yellow-700">
                <Plus size={16} className="mr-2" /> Record Reading
              </Button>
            )}
          </Card>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
            <table className="w-full" data-testid="readings-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="p-3 text-left text-xs font-semibold text-gray-700 uppercase">Date</th>
                  <th className="p-3 text-left text-xs font-semibold text-gray-700 uppercase">Month</th>
                  <th className="p-3 text-left text-xs font-semibold text-gray-700 uppercase">Meter / Property</th>
                  <th className="p-3 text-left text-xs font-semibold text-gray-700 uppercase">Tenant</th>
                  <th className="p-3 text-right text-xs font-semibold text-gray-700 uppercase">Prev → Curr</th>
                  <th className="p-3 text-right text-xs font-semibold text-gray-700 uppercase">Units</th>
                  <th className="p-3 text-right text-xs font-semibold text-gray-700 uppercase">Rate</th>
                  <th className="p-3 text-right text-xs font-semibold text-gray-700 uppercase">Amount</th>
                  <th className="p-3 text-center text-xs font-semibold text-gray-700 uppercase">Paid</th>
                  <th className="p-3 text-right text-xs font-semibold text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredReadings.map(r => (
                  <tr key={r.id} className="border-b hover:bg-gray-50" data-testid={`reading-row-${r.id}`}>
                    <td className="p-3 text-sm">{r.reading_date}</td>
                    <td className="p-3 text-sm"><span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">{r.billing_month}</span></td>
                    <td className="p-3 text-sm">
                      <p className="font-medium text-gray-900">{r.meter_number}{r.meter_label ? ` • ${r.meter_label}` : ''}</p>
                      <p className="text-xs text-gray-500">{r.property_name}{r.room_number ? ` • Room ${r.room_number}` : ''}</p>
                    </td>
                    <td className="p-3 text-sm text-gray-700">{r.tenant_name || '—'}</td>
                    <td className="p-3 text-right text-sm tabular-nums text-gray-700">{r.previous_reading} → <span className="font-semibold">{r.current_reading}</span></td>
                    <td className="p-3 text-right text-sm font-medium tabular-nums text-indigo-700">{r.units_consumed}</td>
                    <td className="p-3 text-right text-sm tabular-nums text-gray-600">₹{r.rate_per_unit}</td>
                    <td className="p-3 text-right text-sm font-semibold tabular-nums text-gray-900">₹{(r.amount || 0).toLocaleString()}</td>
                    <td className="p-3 text-center">
                      <button
                        data-testid={`reading-paid-toggle-${r.id}`}
                        onClick={() => togglePaid(r)}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          r.paid ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-red-50 text-red-700 hover:bg-red-100'
                        }`}
                      >
                        {r.paid ? <CheckCircle2 size={12} /> : <Circle size={12} />}
                        {r.paid ? 'Paid' : 'Unpaid'}
                      </button>
                    </td>
                    <td className="p-3">
                      <div className="flex gap-1 justify-end">
                        <button data-testid={`reading-edit-${r.id}`} onClick={() => { setEditingReading(r); setShowReadingForm(true); }} className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded" title="Edit reading"><Edit2 size={14}/></button>
                        <button data-testid={`reading-delete-${r.id}`} onClick={() => setDeletingReading(r)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded" title="Delete reading"><Trash2 size={14}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        meters.length === 0 ? (
          <Card className="p-12 text-center" data-testid="meters-empty">
            <Gauge size={56} className="mx-auto text-gray-300 mb-3" />
            <h3 className="text-xl font-semibold mb-2">No meters registered</h3>
            <p className="text-gray-600 mb-4">Register your property's main meter or per-room sub-meters to start tracking.</p>
            <Button onClick={() => { setEditingMeter(null); setShowMeterForm(true); }} className="bg-yellow-600 hover:bg-yellow-700">
              <Plus size={16} className="mr-2" /> Add First Meter
            </Button>
          </Card>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
            <table className="w-full" data-testid="meters-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="p-3 text-left text-xs font-semibold text-gray-700 uppercase">Meter</th>
                  <th className="p-3 text-left text-xs font-semibold text-gray-700 uppercase">Property / Room</th>
                  <th className="p-3 text-right text-xs font-semibold text-gray-700 uppercase">Rate ₹/kWh</th>
                  <th className="p-3 text-right text-xs font-semibold text-gray-700 uppercase">Opening</th>
                  <th className="p-3 text-left text-xs font-semibold text-gray-700 uppercase">Latest Reading</th>
                  <th className="p-3 text-right text-xs font-semibold text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {meters.map(m => (
                  <tr key={m.id} className="border-b hover:bg-gray-50" data-testid={`meter-row-${m.id}`}>
                    <td className="p-3">
                      <p className="font-medium text-gray-900">{m.meter_number}</p>
                      {m.label && <p className="text-xs text-gray-500">{m.label}</p>}
                    </td>
                    <td className="p-3 text-sm text-gray-700">
                      {m.property_name}{m.room_number ? ` • Room ${m.room_number}` : ' • Common'}
                    </td>
                    <td className="p-3 text-right text-sm tabular-nums text-gray-700">₹{m.rate_per_unit}</td>
                    <td className="p-3 text-right text-sm tabular-nums text-gray-500">{m.opening_reading}</td>
                    <td className="p-3 text-sm">
                      {m.latest_reading ? (
                        <div>
                          <span className="font-medium text-gray-900">{m.latest_reading.current_reading}</span>
                          <span className="text-xs text-gray-500 ml-2">{m.latest_reading.billing_month}</span>
                        </div>
                      ) : <span className="text-gray-400 text-xs italic">No reading yet</span>}
                    </td>
                    <td className="p-3">
                      <div className="flex gap-1 justify-end">
                        <button
                          data-testid={`meter-record-${m.id}`}
                          onClick={() => { setEditingReading(null); setPreselectMeter(m.id); setShowReadingForm(true); }}
                          className="px-2 py-1 text-xs bg-yellow-50 text-yellow-700 hover:bg-yellow-100 rounded flex items-center gap-1"
                        >
                          <TrendingUp size={12} /> Record
                        </button>
                        <button data-testid={`meter-edit-${m.id}`} onClick={() => { setEditingMeter(m); setShowMeterForm(true); }} className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded" title="Edit meter"><Edit2 size={14}/></button>
                        <button data-testid={`meter-delete-${m.id}`} onClick={() => setDeletingMeter(m)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded" title="Delete meter"><Trash2 size={14}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Meter photo upload hint (paid feature) */}
      <div className="mt-4 text-xs text-gray-400 flex items-center gap-1.5">
        <Camera size={12} />
        Meter-photo upload with AI reading extraction is a <span className="font-medium text-gray-500">Pro-tier</span> feature — coming soon.
      </div>

      {/* Modals */}
      {showMeterForm && (
        <MeterFormModal
          open={showMeterForm}
          onClose={() => { setShowMeterForm(false); setEditingMeter(null); }}
          onSuccess={fetchData}
          editing={editingMeter}
          properties={properties}
          token={token}
        />
      )}
      {showReadingForm && (
        <ReadingFormModal
          open={showReadingForm}
          onClose={() => { setShowReadingForm(false); setEditingReading(null); setPreselectMeter(''); }}
          onSuccess={fetchData}
          editing={editingReading}
          meters={meters}
          preselectMeter={preselectMeter}
          token={token}
        />
      )}

      <DeleteConfirmationModal
        open={!!deletingMeter}
        onClose={() => setDeletingMeter(null)}
        onConfirm={handleDeleteMeter}
        title="Delete Meter"
        message="This will delete the meter AND all its readings. This cannot be undone."
        itemName={deletingMeter?.meter_number || ''}
        loading={deleteLoading}
      />
      <DeleteConfirmationModal
        open={!!deletingReading}
        onClose={() => setDeletingReading(null)}
        onConfirm={handleDeleteReading}
        title="Delete Reading"
        message="Are you sure? Only the most recent reading per meter can be deleted."
        itemName={deletingReading ? `${deletingReading.meter_number} • ${deletingReading.billing_month}` : ''}
        loading={deleteLoading}
      />
    </div>
  );
};

// ---------- Meter Form ----------
const MeterFormModal = ({ open, onClose, onSuccess, editing, properties, token }) => {
  const [form, setForm] = useState({
    property_id: '', room_id: '', meter_number: '', label: '',
    rate_per_unit: 10, opening_reading: 0, notes: '',
  });
  const [rooms, setRooms] = useState([]);
  const [saving, setSaving] = useState(false);
  const h = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (editing) setForm({
      property_id: editing.property_id || '',
      room_id: editing.room_id || '',
      meter_number: editing.meter_number || '',
      label: editing.label || '',
      rate_per_unit: editing.rate_per_unit || 10,
      opening_reading: editing.opening_reading || 0,
      notes: editing.notes || '',
    });
  }, [editing]);

  useEffect(() => {
    if (!form.property_id) { setRooms([]); return; }
    (async () => {
      try {
        const res = await axios.get(`${API}/properties/${form.property_id}/rooms`, h);
        setRooms(res.data);
      } catch { setRooms([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.property_id, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        rate_per_unit: parseFloat(form.rate_per_unit) || 0,
        opening_reading: parseFloat(form.opening_reading) || 0,
        room_id: form.room_id || null,
      };
      if (editing) {
        // Cannot change property_id / opening_reading after creation (ledger integrity)
        const { property_id: _p, opening_reading: _o, ...safe } = payload;
        await axios.put(`${API}/electricity/meters/${editing.id}`, safe, h);
        toast({ title: 'Meter updated' });
      } else {
        await axios.post(`${API}/electricity/meters`, payload, h);
        toast({ title: 'Meter added' });
      }
      onSuccess(); onClose();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>{editing ? 'Edit Meter' : 'Add Electricity Meter'}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="meter-form">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Property *</Label>
              <select data-testid="meter-property" value={form.property_id} onChange={e => setForm({ ...form, property_id: e.target.value, room_id: '' })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required disabled={!!editing}>
                <option value="">Select</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              {editing && <p className="text-xs text-gray-400 mt-1">Property cannot be changed after creation.</p>}
            </div>
            <div>
              <Label>Room (Optional)</Label>
              <select data-testid="meter-room" value={form.room_id} onChange={e => setForm({ ...form, room_id: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" disabled={!form.property_id}>
                <option value="">Common / Property-level</option>
                {rooms.map(r => <option key={r.id} value={r.id}>Room {r.room_number}</option>)}
              </select>
            </div>
            <div>
              <Label>Meter Number *</Label>
              <Input data-testid="meter-number" value={form.meter_number} onChange={e => setForm({ ...form, meter_number: e.target.value })} placeholder="e.g., MTR-101-A" required />
            </div>
            <div>
              <Label>Label (Optional)</Label>
              <Input data-testid="meter-label" value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="e.g., Main, Floor 1" />
            </div>
            <div>
              <Label>Rate (₹ per kWh) *</Label>
              <Input data-testid="meter-rate" type="number" step="0.01" min="0" value={form.rate_per_unit} onChange={e => setForm({ ...form, rate_per_unit: e.target.value })} required />
            </div>
            <div>
              <Label>Opening Reading</Label>
              <Input data-testid="meter-opening" type="number" step="0.01" min="0" value={form.opening_reading} onChange={e => setForm({ ...form, opening_reading: e.target.value })} disabled={!!editing} />
              {editing && <p className="text-xs text-gray-400 mt-1">Locked after creation.</p>}
            </div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <textarea data-testid="meter-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <Button data-testid="meter-submit" type="submit" className="flex-1 bg-yellow-600 hover:bg-yellow-700" disabled={saving}>
              {saving ? 'Saving...' : (editing ? 'Update Meter' : 'Add Meter')}
            </Button>
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Reading Form ----------
const ReadingFormModal = ({ open, onClose, onSuccess, editing, meters, preselectMeter, token }) => {
  const [form, setForm] = useState({
    meter_id: preselectMeter || '',
    current_reading: '',
    reading_date: today(),
    billing_month: currentBillingMonth(),
    tenant_id: '',
    rate_per_unit: '',
    notes: '',
  });
  const [tenants, setTenants] = useState([]);
  const [lastReading, setLastReading] = useState(null);
  const [saving, setSaving] = useState(false);
  const h = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (editing) setForm({
      meter_id: editing.meter_id || '',
      current_reading: editing.current_reading ?? '',
      reading_date: editing.reading_date || today(),
      billing_month: editing.billing_month || currentBillingMonth(),
      tenant_id: editing.tenant_id || '',
      rate_per_unit: editing.rate_per_unit ?? '',
      notes: editing.notes || '',
    });
  }, [editing]);

  // When meter changes, pull latest reading + tenants of that property
  useEffect(() => {
    if (!form.meter_id) { setLastReading(null); setTenants([]); return; }
    const meter = meters.find(m => m.id === form.meter_id);
    if (!meter) return;
    if (!editing) {
      setForm(f => ({ ...f, rate_per_unit: f.rate_per_unit || meter.rate_per_unit }));
    }
    (async () => {
      try {
        const [rRes, tRes] = await Promise.all([
          axios.get(`${API}/electricity/readings?meter_id=${form.meter_id}`, h),
          axios.get(`${API}/tenants?property_id=${meter.property_id}`, h),
        ]);
        const sorted = (rRes.data || []).filter(r => !editing || r.id !== editing.id);
        setLastReading(sorted[0] || null);
        setTenants(tRes.data || []);
      } catch {
        setLastReading(null); setTenants([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.meter_id, token]);

  const previousReading = editing
    ? editing.previous_reading
    : (lastReading ? lastReading.current_reading : (meters.find(m => m.id === form.meter_id)?.opening_reading ?? 0));
  const unitsPreview = form.current_reading !== '' ? Math.max(0, (parseFloat(form.current_reading) || 0) - (previousReading || 0)) : 0;
  const ratePreview = parseFloat(form.rate_per_unit) || 0;
  const amountPreview = unitsPreview * ratePreview;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        meter_id: form.meter_id,
        current_reading: parseFloat(form.current_reading),
        reading_date: form.reading_date,
        billing_month: form.billing_month,
        tenant_id: form.tenant_id || null,
        rate_per_unit: form.rate_per_unit === '' ? null : parseFloat(form.rate_per_unit),
        notes: form.notes || null,
      };
      if (editing) {
        const { meter_id: _m, ...safe } = payload;
        await axios.put(`${API}/electricity/readings/${editing.id}`, safe, h);
        toast({ title: 'Reading updated' });
      } else {
        await axios.post(`${API}/electricity/readings`, payload, h);
        toast({ title: 'Reading recorded', description: `₹${amountPreview.toFixed(2)} billable for ${unitsPreview.toFixed(2)} units` });
      }
      onSuccess(); onClose();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>{editing ? 'Edit Reading' : 'Record Meter Reading'}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="reading-form">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Meter *</Label>
              <select data-testid="reading-meter" value={form.meter_id} onChange={e => setForm({ ...form, meter_id: e.target.value, tenant_id: '' })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required disabled={!!editing}>
                <option value="">Select meter</option>
                {meters.map(m => (
                  <option key={m.id} value={m.id}>
                    {m.meter_number}{m.label ? ` • ${m.label}` : ''} — {m.property_name}{m.room_number ? ` (Room ${m.room_number})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {form.meter_id && (
              <div className="col-span-2 bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm">
                <p className="text-gray-700">
                  Previous reading: <span className="font-semibold">{previousReading}</span>
                  {lastReading && <span className="text-xs text-gray-500 ml-2">(from {lastReading.billing_month} · {lastReading.reading_date})</span>}
                  {!lastReading && !editing && <span className="text-xs text-gray-500 ml-2">(meter opening reading)</span>}
                </p>
              </div>
            )}

            <div>
              <Label>Billing Month *</Label>
              <Input data-testid="reading-month" type="month" value={form.billing_month} onChange={e => setForm({ ...form, billing_month: e.target.value })} required />
            </div>
            <div>
              <Label>Reading Date *</Label>
              <Input data-testid="reading-date" type="date" value={form.reading_date} onChange={e => setForm({ ...form, reading_date: e.target.value })} required />
            </div>
            <div>
              <Label>Current Reading *</Label>
              <Input data-testid="reading-current" type="number" step="0.01" min={previousReading} value={form.current_reading} onChange={e => setForm({ ...form, current_reading: e.target.value })} placeholder={`≥ ${previousReading}`} required />
            </div>
            <div>
              <Label>Rate (₹/kWh)</Label>
              <Input data-testid="reading-rate" type="number" step="0.01" min="0" value={form.rate_per_unit} onChange={e => setForm({ ...form, rate_per_unit: e.target.value })} placeholder="Inherits meter rate" />
            </div>
            <div className="col-span-2">
              <Label>Bill Tenant (Optional)</Label>
              <select data-testid="reading-tenant" value={form.tenant_id} onChange={e => setForm({ ...form, tenant_id: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option value="">None — property-level bill</option>
                {tenants.map(t => <option key={t.id} value={t.id}>{t.name}{t.room_number ? ` • Room ${t.room_number}` : ''}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <textarea data-testid="reading-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
          </div>

          {/* Auto-compute preview */}
          {form.meter_id && form.current_reading !== '' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 grid grid-cols-3 gap-2 text-center" data-testid="reading-preview">
              <div>
                <p className="text-xs text-gray-500">Units</p>
                <p className="text-lg font-bold text-gray-900">{unitsPreview.toFixed(2)} <span className="text-xs text-gray-400">kWh</span></p>
              </div>
              <div>
                <p className="text-xs text-gray-500">× Rate</p>
                <p className="text-lg font-bold text-gray-900">₹{ratePreview.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Bill Amount</p>
                <p className="text-lg font-bold text-green-700">₹{amountPreview.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button data-testid="reading-submit" type="submit" className="flex-1 bg-yellow-600 hover:bg-yellow-700" disabled={saving}>
              {saving ? 'Saving...' : (editing ? 'Update Reading' : 'Record Reading')}
            </Button>
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default Electricity;
